import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isObject } from 'lib';
import { apiGet, apiPost } from 'api-service';
import {
  handleSetSelectedFilterIds,
  handleSetSelectedItems,
  handleToggleCollapsed,
  handleSetSelectedFilterValue,
  handleResetFilter,
} from 'store/helpers/filterHelpers';
import { kioskFiltersInitialState } from './kioskInitialState';

export const getKioskSearchParamsFromFilters = (filters = {}) => {
  const isFilterDisabled = (filterName) =>
    isObject(filters?.[filterName]) ? filters[filterName].isDisabled : true;

  const params = {
    kiosk_categories: isFilterDisabled('subjectFilter')
      ? undefined
      : filters?.subjectFilter?.selectedIds,
    [filters?.resourceCategoryFilter?.filterType === 'include'
      ? 'tag_id'
      : 'not_tag_id']: isFilterDisabled('resourceCategoryFilter')
      ? undefined
      : filters?.resourceCategoryFilter?.selectedValue || undefined,
    [filters?.resourcesFilter?.selectedValue === 'include'
      ? 'resource_ids'
      : 'not_resource_ids']: isFilterDisabled('resourcesFilter')
      ? undefined
      : Object.keys(filters?.resourcesFilter?.selectedItems || {}),
  };

  Object.keys(params).forEach((key) => {
    if (Array.isArray(params[key]) && !params[key].length) {
      delete params[key];
    }
  });

  return params;
};

export const fetchNewspapers = createAsyncThunk(
  'kioskReducer/fetchNewspapers',
  async (payload, thunkAPI) => {
    const kioskFilters = thunkAPI.getState().kioskReducer.filters;
    const params = {
      ...payload,
      ...getKioskSearchParamsFromFilters(kioskFilters),
    };
    try {
      const response = await apiGet('/search/api/v1/search', params);
      const sortedNewspapers = response.data.result.data.sort((newspaper) =>
        newspaper.is_bookmarked ? -1 : 1
      );
      return sortedNewspapers;
    } catch (error) {
      thunkAPI.rejectWithValue(error);
    }
  }
);

export const toggleNewspaperBookmark = createAsyncThunk(
  'kioskReducer/toggleNewspaperBookmark',
  async (payload) => {
    const response = await apiPost('/search/api/v1/kiosk/newspaper/bookmark', {
      id: payload.id,
      action: payload.action,
    });
    return response.data.result.data;
  }
);

export const kioskInitialState = {
  newspapers: {
    data: [],
    loading: true,
    error: null,
  },
  filters: kioskFiltersInitialState,
  selectedFilter: 'subjectFilter',
};

export const kioskSlice = createSlice({
  name: 'kioskSlice',
  initialState: kioskInitialState,
  reducers: {
    setSelectedFilterIds: (state, action) => {
      handleSetSelectedFilterIds(state, action);
    },
    setSelectedItems: (state, action) => {
      handleSetSelectedItems(state, action);
    },
    setSelectedFilterValue: (state, action) => {
      handleSetSelectedFilterValue(state, action, kioskFiltersInitialState);
    },
    toggleCollapsed: (state, action) => {
      handleToggleCollapsed(state, action);
    },
    resetAllFilters: (state) => {
      state.filters = kioskFiltersInitialState;
    },
    resetFilter: (state, action) => {
      handleResetFilter(state, action, kioskFiltersInitialState);
    },
    setSelectedFilter: (state, action) => {
      state.selectedFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNewspapers.fulfilled, (state, action) => {
        state.newspapers.data = action.payload || [];
        state.newspapers.loading = false;
        state.newspapers.error = null;
      })
      .addCase(fetchNewspapers.pending, (state) => {
        state.newspapers.loading = true;
        state.newspapers.error = null;
      })
      .addCase(fetchNewspapers.rejected, (state, action) => {
        state.newspapers.loading = false;
        state.newspapers.error = action.payload;
      })
      .addCase(toggleNewspaperBookmark.fulfilled, (state, action) => {
        const toggledNewspaperId = action.meta.arg.newspaperId;
        state.newspapers.data.forEach((newspaper, index) => {
          if (newspaper.id === toggledNewspaperId) {
            state.newspapers.data[index].is_bookmarked =
              !state.newspapers.data[index].is_bookmarked;
          }
        });
      });
  },
});

export const {
  toggleCollapsed,
  setSelectedItems,
  setSelectedFilterIds,
  setSelectedFilterValue,
  resetFilter,
  resetAllFilters,
  setSelectedFilter,
} = kioskSlice.actions;
